// @ts-strict-ignore
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ThunderboltFilled } from '@ant-design/icons';
import Progress from 'antd/lib/progress';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { AppleAppStorePhasedReleaseState, AutomationAutoStagedRollout, PhasedRelease } from '../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { PausedByBlackoutBadge } from '../monitoring/PhasedReleaseCard';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';
import { selectAutomationById } from '@/selectors/selectors';
import { Nullable } from '@/typescriptHelpers';

interface PhasedReleaseProgressProps extends PhasedRelease {
    showPercent?: boolean;
    appId: string;
    progressStyle?: React.CSSProperties;
    phasedReleasePaused?: boolean;
    isAutoAccelerated?: boolean;
    isAutoHalted?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const iconForPhasedReleaseProgressAndDay = (phasedReleaseProgress: PhasedReleaseProgressProps, dayNumber: number) => {
    if (phasedReleaseProgress.dayNumber === dayNumber) {
        if (phasedReleaseProgress.isAutoAccelerated || phasedReleaseProgress.isAutoHalted) {
            return <ThunderboltFilled style={{ color: runwayColors.primary }} />;
        } else if (phasedReleaseProgress.isOnBlackoutPeriod && !!phasedReleaseProgress.blackoutEndsAt) {
            return <PausedByBlackoutBadge phasedRelease={phasedReleaseProgress} isSizeSmall />;
        }
    }

    return <div style={{ minWidth: 14, height: 14 }} />;
};

export const PhasedReleaseProgress: React.FC<PhasedReleaseProgressProps> = (props: PhasedReleaseProgressProps) => {
    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const customDays = daysToPercent
        ?.map((percent, index) => [index + 1, percent])
        .slice()
        .reverse() as [number, number][];

    const dayPairs: [number, number][] =
        customDays ?? [7, 6, 5, 4, 3, 2, 1].map((day) => [day, applePhasedReleasePercentByDay(day)]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: 240,
                position: 'relative',
            }}
        >
            {dayPairs.map(([dayNumber, phasedPercentByDay]) => {
                let phasedPercent = phasedPercentByDay;
                const isCompletedDay = dayNumber <= props.dayNumber;
                const isRolloutAccelerated =
                    props.isManuallyAcceleratedPhasedRollout || props.isRunwayAcceleratedRollout;

                if (
                    props.dayNumber === dayNumber &&
                    isRolloutAccelerated &&
                    props.state === AppleAppStorePhasedReleaseState.Complete
                ) {
                    // accelerated to 100% on this day
                    phasedPercent = 100;
                }

                let strokeColor = antGrey.grey6;

                if (isCompletedDay) {
                    strokeColor = runwayColors.primary;
                    if (props.dayNumber === dayNumber) {
                        if (props.phasedReleasePaused || props.isAutoHalted) {
                            strokeColor = runwayColors.statusRed;
                        } else if (props.isAutoAccelerated) {
                            strokeColor = runwayColors.statusGreen;
                        }
                    }
                }

                const isSkippedDay =
                    props.dayNumber < dayNumber &&
                    isRolloutAccelerated &&
                    props.state === AppleAppStorePhasedReleaseState.Complete;

                if (isSkippedDay) {
                    phasedPercent = 100;
                    strokeColor = antGrey.grey6;
                }

                return (
                    <div
                        key={dayNumber}
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {iconForPhasedReleaseProgressAndDay(props, dayNumber)}
                        <Tooltip placement="right" title={`${phasedPercent}%`}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    height: 16,
                                    flexGrow: 1,
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontSize: 10,
                                        lineHeight: '10px',
                                        textTransform: 'uppercase',
                                        marginLeft: 8,
                                        letterSpacing: '0.1em',
                                        flexShrink: 0,
                                    }}
                                >
                                    Day {dayNumber}
                                </Text>
                                <Progress
                                    size="small"
                                    percent={phasedPercent}
                                    trailColor={antGrey.grey5}
                                    strokeColor={strokeColor}
                                    style={{ ...props.progressStyle }}
                                    showInfo={props.showPercent}
                                    format={(percent) => {
                                        return (
                                            <Text
                                                style={{
                                                    color: antGrey.grey7,
                                                    fontSize: 10,
                                                    lineHeight: '10px',
                                                    textTransform: 'uppercase',
                                                    marginRight: 8,
                                                    letterSpacing: '0.1em',
                                                }}
                                            >
                                                {percent}%
                                            </Text>
                                        );
                                    }}
                                    status={'normal'}
                                />
                            </div>
                        </Tooltip>
                    </div>
                );
            })}
        </div>
    );
};

export const applePhasedReleasePercentByDay = (dayNumber: number) => {
    if (dayNumber === 0) {
        return 0;
    } else if (dayNumber === 1) {
        return 1;
    } else if (dayNumber === 2) {
        return 2;
    } else if (dayNumber === 3) {
        return 5;
    } else if (dayNumber === 4) {
        return 10;
    } else if (dayNumber === 5) {
        return 20;
    } else if (dayNumber === 6) {
        return 50;
    } else if (dayNumber === 7) {
        return 100;
    } else {
        return 100;
    }
};
